import React, { forwardRef, Fragment, useState, useEffect } from "react";
import { Icon, Button } from 'antd';
import Row from "antd/es/row/index";
import Col from "antd/es/col/index";
import TranslatedText from "translations/TranslatedText";
import Input from "antd/es/input/Input";
import Checkbox from "antd/es/checkbox/index";
import cloneDeep from "lodash.clonedeep";
import SentenceComponent from './SentenceComponent'
import { Divider } from 'antd';
import { numToWord } from 'lib';
import Paragraph from 'antd/es/typography/Paragraph';

export default forwardRef((props: any, ref: any) => {
  const {
    sentences: defaultSentences,
    language,
    onChange,
  } = props;
  const [allSentences, setAllSentences] = useState(defaultSentences);
  const [localSentences, setLocalSentences] = useState(allSentences[language]);

  const setHanzi = (hanzi, index) => {
    const localSentencesClone = cloneDeep(localSentences)
    localSentencesClone[index].hanzi = hanzi
    setLocalSentences(localSentencesClone)
  }

  const setPinyin = (hanzi, index) => {
    const localSentencesClone = cloneDeep(localSentences)
    localSentencesClone[index].pinyin = hanzi
    setLocalSentences(localSentencesClone)
  }

  const setCorrectResponse = (blank, sentenceIndex, blankIndex) => {
    const localSentencesClone = cloneDeep(localSentences)
    localSentencesClone[sentenceIndex].blank = blank
    setLocalSentences(localSentencesClone)

    const correctAnswers = blank[blankIndex].split('/')
    const hanzi = correctAnswers[0]
    const pinyin = correctAnswers[1]
    const allSentencesClone = cloneDeep(allSentences)
    allSentencesClone.correctAnswers.hanzi[blankIndex] = hanzi
    allSentencesClone.correctAnswers.pinyin[blankIndex] = pinyin
    setAllSentences(allSentencesClone)
  }

  const setIsBlank = (event, index) => {
    const localSentencesClone = cloneDeep(localSentences)
    localSentencesClone[index].isBlank = event.target.checked
    setLocalSentences(localSentencesClone)
  }

  const addText = (index) => {
    const localSentencesClone = cloneDeep(localSentences)
    localSentencesClone[index].hanzi.push("")
    localSentencesClone[index].pinyin.push("")
    setLocalSentences(localSentencesClone);
  }

  const addBlank = (index) => {
    const localSentencesClone = cloneDeep(localSentences)
    localSentencesClone[index].hanzi.push(-1)
    localSentencesClone[index].pinyin.push(-1)
    setLocalSentences(localSentencesClone);
  }

  const remove = (wordIndex, sentenceIndex) => {
    const localSentencesClone = cloneDeep(localSentences)
    localSentencesClone[sentenceIndex].hanzi.splice(wordIndex, 1)
    localSentencesClone[sentenceIndex].pinyin.splice(wordIndex, 1)

    if (localSentencesClone[sentenceIndex].blank[wordIndex]) {
      localSentencesClone[sentenceIndex].blank.splice(wordIndex, 1)
    }

    setLocalSentences(localSentencesClone);
  }

  const onSetUsingSyntaxConfig = (event) => {
    const allSentencesClone = cloneDeep(allSentences);
    allSentencesClone.syntaxConfig = event.target.checked;
    setAllSentences(allSentencesClone);
  }

  const changeSyntaxBlank = (value: string, index: number) => {
    const allSentencesClone = cloneDeep(allSentences);
    allSentencesClone.blanks[index].answer = value;
    setAllSentences(allSentencesClone);
  }

  const addSyntaxBlank = () => {
    const allSentencesClone = cloneDeep(allSentences);
    allSentencesClone.blanks.push({
      answer: "",
    })
    setAllSentences(allSentencesClone);
  }

  const removeSyntaxBlank = (index) => {
    const allSentencesClone = cloneDeep(allSentences);
    allSentencesClone.blanks.splice(index, 1);
    setAllSentences(allSentencesClone);
  }

  useEffect(() => {
    let isMounted = true;

    const allSentencesClone = cloneDeep(allSentences)
    allSentencesClone[language] = localSentences
    setAllSentences(allSentencesClone)

    return () => { isMounted = false }
  }, [localSentences])

  useEffect(() => {
    onChange(allSentences)
  }, [allSentences])

  return (
    <Fragment>
      <Row type="flex">
        <Col>
          <Checkbox checked={allSentences.syntaxConfig}
                    onChange={onSetUsingSyntaxConfig}
          />
        </Col>
        <Col push={1}>
          <TranslatedText id="settings.form.fillblanktext.config.syntax" />
        </Col>
      </Row>
      {
        allSentences.syntaxConfig &&
        <Fragment>
          <Row>
            <Col>
              <TranslatedText id="settings.form.fillblanktext.config.editable" />
            </Col>
          </Row>
          <Row>
            <Col>
              {
                allSentences.blanks.map((blank, index) => {
                  return <Fragment key={index}>
                    <Row type="flex" justify="start" align="middle">
                      <Col span={20}>
                        <Input
                          onChange={(event)=>changeSyntaxBlank(event.target.value, index)}
                          style={{ minWidth: '15vw'}}
                          value={blank.answer}
                        />
                      </Col>
                      <Col>
                        <Fragment>
                          <Button onClick={()=>addSyntaxBlank()}>
                            <Icon type="plus-circle" />
                          </Button>
                          {allSentences.blanks.length > 1 && (
                            <Button onClick={()=>removeSyntaxBlank(index)}>
                              <Icon type="minus-circle" />
                            </Button>
                          )}
                        </Fragment>
                      </Col>
                    </Row>
                  </Fragment>
                })
              }
            </Col>
          </Row>
        </Fragment> 
      }
      {
        !allSentences.syntaxConfig && localSentences.map((sentence, index) => {
          return (
            <Fragment key={index}>
              <Divider orientation="center">{numToWord(index+1)} sentence</Divider>
              <Button onClick={()=>addText(index)}>
                <Icon type="plus-circle" />
                <TranslatedText id="settings.form.fillblanktext.sentence.btn.text.add" />
              </Button>
              <Button onClick={()=>addBlank(index)}
                      style={{ marginBottom: 12}}
              >
                <Icon type="dash" />
                <TranslatedText id="settings.form.fillblanktext.sentence.btn.blank.add" />
              </Button>
              <SentenceComponent
                sentence={sentence}
                onSetHanzi={(hanzi)=>setHanzi(hanzi, index)}
                onSetPinyin={(hanzi)=>setPinyin(hanzi, index)}
                onSetBlank={(blank, blankIndex)=>setCorrectResponse(blank, index, blankIndex)}
                onRemove={(wordIndex)=>remove(wordIndex, index)}
              />
            </Fragment>
          )
        })
      }
    </Fragment>
  );
});
